import { encode, decode } from './he';

// FOUR things differ from the original and they are a matched SET, not
// independent tweaks. Read the "why they are a set" note before touching any of
// them: three separate rounds of review each fixed one, shipped, and were then
// found to have moved the blowup to the next class rather than removed it.
//
// 1. The unquoted attribute VALUE is `[^>\s"']+`, not `[^>\s/]+`. Dropping `/`
//    from the exclusions is the actual bug fix: the HTML tokenizer treats `/` as
//    an ordinary character in the unquoted-value state, so `<img src=x
//    onerror=alert(1)//>` is one tag with two live attributes to a browser,
//    while the original class made the whole tag fail to match.
//
// 2. The attribute NAME is `[^\s=>/"']+`.
// 3. The TAG NAME is `[^\s>"']+?`, not `[\S]+?`.
//    Quotes are legal in neither per the HTML spec, so 2 and 3 lose nothing.
//    Both are load-bearing for the same reason as 1 (below).
//
// 4. The attribute separator is `(?:\s+|(?<=["']))`: whitespace, OR nothing at
//    all when the previous character closed a quoted value. That covers
//    `<td width="100%"bgcolor="#fff">`, which omits the space after a quoted
//    value; it is valid HTML5 and is what email minifiers emit.
//
// Why they are a set. 4 introduces a ZERO-WIDTH separator, so it can start an
// attribute run at any position that follows a quote. Every class that can
// itself consume a quote therefore becomes a second way to parse the same text,
// and the engine tries all of them on input it cannot anchor: the cost is
// exponential in the token count. 4 is what makes 1, 2 and 3 mandatory rather
// than cosmetic. Each was found only after the previous one was fixed:
//
//   - VALUE vs the quoted alternative: `a="b/c"` readable as the quoted
//     alternative OR as one unquoted value. `<a ` + `a="b/c" ` x22 (179 bytes)
//     took 4 381 ms, no return at x24.
//   - NAME vs the zero-width separator: one name, OR name + zero-width + name,
//     two parses per quote. Needs no crafted payload: 226 bytes of ordinary
//     French copy (`<p>Prix <900'000 CHF, ` + `l'agent d'une agence n'a ` x8)
//     took 6 179 ms against 0.6 ms on the original. Apostrophes are not an
//     attack.
//   - TAG NAME vs the zero-width separator: `[\S]+?` could expand across a
//     quote, so every prefix ending in one gave the run a fresh start.
//     `<` + `<x="y"` x1024 (6 145 bytes) took 10 447 ms against 31 ms on the
//     original, and a benign `<html><body><p>...` prefix did not rescue it.
//
// With all four, every shape above is flat: 0.7-1.9 ms, and still flat at 4x the
// length. That matters because this package renders during aggregator SSR, where
// blocking the event loop stalls every visitor served by that process, and it
// reaches attacker-supplied HTML through `UnfoldedEmailMessage` (inbound mail).
//
// Rejected: a `[\s/]+` separator (lets a run cross a tag boundary, so
// `<p>Surface <120 m2</p>` matched as one tag named `120` and the catch-all
// predicate deleted the text: prices written `<900'000 CHF` are ordinary listing
// copy); and chaining quoted attributes as a second alternation branch
// (ambiguous again, hangs).
//
// NOT a reason to reject the atomic run `(?=(X*))\3`, recorded because an
// earlier revision of this comment said it was and that was measured false: on
// top of the four classes above, atomic passes the whole suite AND handles the
// minifier shape. What broke that shape was the plain `\s+` separator it was
// paired with at the time, not atomicity. Atomicity is simply redundant now
// that the classes are disjoint, so it is left out as dead weight rather than
// as a hazard.
//
// Two behaviour changes are deliberate and worth naming, because they are not
// bug fixes:
//
//   - `<img src=a.png/>` now yields `src="a.png/"` where the original gave
//     `src="a.png"`. The `/` is part of the value for the tokenizer, so this
//     matches what a browser does and the original did not, but content
//     authored XHTML-style renders differently than it used to.
//   - an unquoted value containing a quote (`<div title=a"b>`) no longer
//     matches, so the tag is escaped to visible text and its closing tag is
//     dropped, where the original emitted `title="a&quot;b"`. The tokenizer
//     treats that quote as a parse error but keeps it in the value, so a browser
//     does render the element. This is the price of 1, it is a regression for
//     that (rare, malformed) shape, and it fails closed rather than open.
//
// The quadratic-in-LENGTH blowup on long `>`-free runs, stated carefully because
// the obvious controls give the wrong answer. On shapes where the ORIGINAL also
// backtracks, the two are comparable: `<a b=x ` x8000 (56 kB) 2 637 ms original
// vs 2 779 ms here, `a<b` x8000 (24 kB) 489 vs 907.
//
// But change 1 genuinely WIDENS the trigger set, and quoting only those controls
// hides it. An unquoted value containing `/` is flat on the original (it is
// excluded from the value class, which is precisely the bug) and quadratic here:
//
//   `<a href=http://a.example/x ` x8000 (211 kB)   original 2 ms | here 9 461 ms
//   `<x a=b/c ` x8000 (70 kB)                      original 1 ms | here 4 163 ms
//
// That is the unavoidable price of matching the tokenizer: the class cannot
// exclude `/` without reopening the verbatim-passthrough hole in 1. The
// thresholds are the same order as the blowup the package already had, so it is
// not a new exposure class, but it is a wider trigger set and not a wash.
// Bounding it needs an input cap at the call sites, which is a product decision
// rather than a sanitizer one.
//
// Built with `new RegExp` rather than written as a literal because `(?<=…)` is
// ES2018. This package is in realadvisor.aggregator's `next-transpile-modules`
// and is imported by client components (`AggregatesListingCard`, `controls/
// marked`); that app is Next 12 with no browserslist, so its client bundle
// targets Safari 12, where a lookbehind LITERAL is a parse-time SyntaxError
// that would take the whole chunk down rather than degrade. As a constructed
// regex the module parses everywhere, and engines without lookbehind (Safari
// <= 16.3) get the plain `\s+` form.
//
// What the fallback costs, stated precisely because it is a real difference and
// not just a lost improvement: it does not match the minifier shape from 3, so
// `<td width="100%"bgcolor="#fff">cell</td>` is neutralised to text AND its
// `</td>` is dropped, where the ORIGINAL rendered that table correctly (by
// accident: its value class swallowed `"100%"bgcolor="#fff"` whole and `rattrs`
// re-split it). So on those clients a minified marketing table degrades to
// visible markup. It is safe -- the walk below escapes what the regex does not
// match, so this fails closed -- but it is a conformance regression for that
// population, not a no-op. The fallback is otherwise unambiguous and flat on
// every hostile shape above.
const tagRePattern = (separator: string) =>
  `<([/])?([^\\s>"']+?)((?:${separator}[^\\s=>/"']+(?:\\s*=\\s*(?:(?:"[^"]*")|(?:'[^']*')|[^>\\s"']+))?)*?)\\s*(/?)\\s*>`;

// The two separator variants `tagRe` can resolve to. Named (and exported below
// for the test suite) because on Node the lookbehind construction never throws,
// so nothing reaches the fallback through the public API, while Safari <= 16.3
// clients run exactly that branch via the aggregator's client bundle.
const TAG_RE_SEPARATORS = {
  lookbehind: `(?:\\s+|(?<=["']))`,
  fallback: `\\s+`,
} as const;

const buildTagRe = (separator: string): RegExp =>
  new RegExp(tagRePattern(separator), 'gim');

const tagRe = (() => {
  try {
    return buildTagRe(TAG_RE_SEPARATORS.lookbehind);
  } catch {
    return buildTagRe(TAG_RE_SEPARATORS.fallback);
  }
})();
const tagComments = /<!--[\s\S]*?-->/gim;

var rattrs =
  /([^\s=]+)(?:\s*=\s*(?:(?:"((?:[^"])*)")|(?:'((?:[^'])*)')|([^>\s]+)))?/gim;

const cleanAttributeName = (name: string) => {
  // attribute can have any non tab, form-feed name, but we
  // use more strict check it can be \w:-
  var re = /[^\w\d:-]+/gim;
  return name.replace(re, '').toLowerCase();
};

type Options = {
  tagsBlacklist: ReadonlyArray<string | ((item: string) => boolean)>;
  attrsBlacklist: ReadonlyArray<string | ((item: string) => boolean)>;
  attrsWithScheme: ReadonlyArray<string | ((item: string) => boolean)>;
  allowedSchemes: ReadonlyArray<string | ((item: string) => boolean)>;
  selfClosingTags: ReadonlyArray<string | ((item: string) => boolean)>;
};

const DEFAULT_OPTIONS = {
  tagsBlacklist: [
    'script',
    'style',
    'iframe',
    'object',
    'meta',
    // None of these carry content, and each is a live vector in a rendered
    // email body: `base` rewrites every relative URL on the page, `embed` and
    // `applet` load executable external content, `link` pulls remote CSS, and
    // `form` turns a message into a credential-harvesting surface.
    'base',
    'embed',
    'link',
    'form',
    'applet',
    (tagName: string) => {
      const reTag = /^[a-zA-Z]+\w*$/i;
      return !reTag.test(tagName);
    },
  ],

  attrsBlacklist: [
    'poster',
    'repeat',
    'pattern',
    'srcset',
    (attrName: string) => attrName.startsWith('on'),
  ],

  // `action` alongside `formaction`: both carry a URL that the browser will
  // navigate to on submit, so both need the scheme check.
  attrsWithScheme: ['action', 'formaction', 'href', 'xlink:href'],
  allowedSchemes: [
    'http',
    'https',
    'mailto',
    'tel',
    (attrValue: string) => !attrValue.includes(':'),
  ],
  selfClosingTags: ['br'],
};

const isSchemaAllowed = (
  value: string,
  allowedSchemes: ReadonlyArray<string | ((item: string) => boolean)>,
): boolean =>
  allowedSchemes.some(schemePrefixOrTestFn => {
    if (typeof schemePrefixOrTestFn === 'string') {
      return value.startsWith(`${schemePrefixOrTestFn}:`);
    }
    return schemePrefixOrTestFn(value);
  });

const isListHasItem = (
  name: string,
  list: ReadonlyArray<string | ((string: string) => boolean)>,
) =>
  list.some(itemNameOrTestFn => {
    if (typeof itemNameOrTestFn === 'string') {
      return itemNameOrTestFn === name;
    }
    return itemNameOrTestFn(name);
  });

// The core takes its tokenizer as a parameter so the test suite can run the
// exact same pipeline on both separator branches; production always passes the
// module-level `tagRe` through the public exports below.
const sanitizeHtmlWithErrorsUsing = (
  tagRe: RegExp,
  str: string,
  argOptions?: Options,
): {
  errors: ReadonlyArray<string>;
  value: string;
} => {
  let errors: string[] = [];
  const stack: string[] = [];
  const options = {
    ...DEFAULT_OPTIONS,
    ...argOptions,
  };

  const replaceTag = (
    _match: string,
    closeSym: string,
    tagName: string,
    attrs: string,
    selfCloseSym: string,
  ) => {
    // Same rule as for attribute names below: the blacklist is lowercase and
    // matched with `===`, while the tag is emitted with its original spelling.
    // HTML tag names are case-insensitive, so testing the raw name let
    // `<SCRIPT>` and `<IFRAME SRC="javascript:...">` through untouched — the
    // catch-all predicate does not help either, since `/^[a-zA-Z]+\w*$/i`
    // happily accepts `SCRIPT`.
    if (isListHasItem(tagName.toLowerCase(), options.tagsBlacklist)) {
      return '';
    }

    if (closeSym === '/') {
      // fix html if closed tags don't match
      const idx = stack.lastIndexOf(tagName);
      let r = '';
      if (idx > -1) {
        for (let i = stack.length - 1; i >= idx; i -= 1) {
          r += `</${stack[i]}>`;
          stack.pop();
        }
        return r;
      }

      return '';
    }

    rattrs.lastIndex = 0;
    let result: RegExpExecArray | null;
    const attrsArr = [];
    while ((result = rattrs.exec(attrs))) {
      const [
        ,
        attrName,
        attrQuoteValue,
        attrSingleQuoteValue,
        attrNoQuoteValue,
      ] = result;

      if (attrName == null) {
        continue;
      }

      // Every check below must run on `cleanName`, never on the raw `attrName`:
      // `cleanName` is what actually gets emitted (see the push at the end of
      // this block), and it is lowercased. Matching the blacklist against the
      // raw name let `<img src=x ONERROR=...>` through the on* filter and then
      // emitted it as an executable `onerror`, and kept `HREF="javascript:..."`
      // past the scheme check. The lists are lowercase, the matcher is `===`.
      const cleanName = cleanAttributeName(attrName);

      if (isListHasItem(cleanName, options.attrsBlacklist)) {
        continue;
      }

      if (cleanName === '') {
        continue;
      }

      const { decoded, errors: errorsDec } = decode(
        attrQuoteValue || attrSingleQuoteValue || attrNoQuoteValue || '',
      );

      const { encoded, errors: errorsEnc } = encode(decoded);

      errors = errors.concat(errorsDec, errorsEnc);

      if (
        isListHasItem(cleanName, options.attrsWithScheme) &&
        !isSchemaAllowed(encoded, options.allowedSchemes)
      ) {
        continue;
      }

      attrsArr.push(encoded ? `${cleanName}="${encoded}"` : `${cleanName}`);
    }

    if (
      selfCloseSym !== '/' &&
      options.selfClosingTags.every(v => v !== tagName.toLowerCase())
    ) {
      stack.push(tagName);
    }

    const newAttrs = attrsArr.join(' ');
    return newAttrs === ''
      ? selfCloseSym === '/' &&
        options.selfClosingTags.some(v => v === tagName.toLowerCase())
        ? `<${tagName}/>`
        : `<${tagName}>${selfCloseSym === '/' ? `</${tagName}>` : ''}`
      : `<${tagName} ${newAttrs}>${
          selfCloseSym === '/' ? `</${tagName}>` : ''
        }`;
  };

  // Walked match by match rather than with a bare `.replace(tagRe, replaceTag)`,
  // so the text BETWEEN matches can be neutralised. `.replace()` is not total:
  // where the regex and the HTML tokenizer disagree about a tag's boundaries the
  // non-matching source is copied out VERBATIM, so a tag the browser parses
  // happily skips `replaceTag` and every check inside it. Every bug found in
  // this file has been an instance of that single class, and each earlier fix
  // moved the disagreement rather than removing it:
  //
  //   <img src=x onerror=alert(1)//>       `/` excluded from unquoted values
  //   <img src=x onerror="alert(1)>"y>     quoted value swallowing the tag's `>`
  //   <a b=x <a b=x ...                    no `>` at all, nothing to anchor on
  //
  // A `<` left standing in a gap is exactly such a leftover, so escaping it
  // makes the residue inert text instead of live markup. That flips the failure
  // mode from open to closed for the whole class, not just for the shape that
  // happened to be reported. The gaps are the only safe place to do it: what
  // `replaceTag` returns is this function's own sanitized output and must be
  // left alone, and a post-pass over the joined string cannot tell the two
  // apart.
  //
  // The lookahead keeps prose intact, and its class is exactly the tokenizer's
  // tag-open state: an ASCII letter starts a tag name, `/` an end tag, `!` a
  // markup declaration, and `?` a BOGUS COMMENT. Anything else is character
  // data, so `a < b`, `5 < 10` and `<3` are untouched, while a literal `x<y`
  // becomes `x&lt;y` and renders as the character the author typed.
  //
  // `?` is in the class for content reasons rather than security ones: a comment
  // cannot become markup, but it runs to the next `>` and eats what it crosses.
  // Left raw, `<p>a<?b</p>` lost `<?b` AND its closing tag to the comment.
  const neutralise = (text: string) =>
    text.replace(/<(?=[/!?a-zA-Z])/g, '&lt;');

  const source = str.replace(tagComments, '');

  // Every match is collected BEFORE `replaceTag` runs for any of them, which is
  // what `String.prototype.replace` does too: `RegExp.prototype[Symbol.replace]`
  // drains the regex into a list first and only then calls the replacer. Keeping
  // that order matters because the production `tagRe` is module-level and `/g`
  // (the parameter above only ever receives that instance, or a private copy in
  // the tests), so its `lastIndex` is shared state, and `replaceTag` calls back
  // into caller-supplied
  // predicates (`tagsBlacklist` and friends are typed to accept functions). A
  // predicate that re-entered `sanitizeHtml` while we were mid-walk would reset
  // `lastIndex` under us: the cursor would rewind and content would be emitted
  // twice, or, if it re-entered on every tag, `exec` would restart from 0 for
  // ever and the call would hang. Draining first makes the walk immune, and it
  // also means a throw from a predicate can no longer leave `lastIndex` dirty,
  // because the regex is already finished with by then.
  //
  // No consumer in this repo passes function predicates today, so this is about
  // not regressing a property the bare `.replace()` used to give for free.
  // `rattrs` is still driven inline inside `replaceTag` and remains re-entrancy
  // sensitive, but that is pre-existing and unchanged here.
  const matches: RegExpExecArray[] = [];
  let match: RegExpExecArray | null;
  tagRe.lastIndex = 0;
  while ((match = tagRe.exec(source)) !== null) {
    // A zero-length match would spin forever on the same index. `tagRe` needs at
    // least `<` and `>` so this is unreachable, and it is kept as a cheap guard
    // rather than an assertion nobody would notice failing.
    if (match.index === tagRe.lastIndex) {
      tagRe.lastIndex += 1;
      continue;
    }
    matches.push(match);
  }

  let r = '';
  let cursor = 0;
  for (const collected of matches) {
    r += neutralise(source.slice(cursor, collected.index));
    // The `?? ''` is for consumers compiling with `noUncheckedIndexedAccess`
    // (docs, auth, clickhouse — this package is compiled from source there).
    // Only group 1 (`([/])?`) can actually be undefined, when the tag is not a
    // closing one; `''` is equivalent since `closeSym` is only ever compared to
    // `'/'`. Groups 2-4 always participate. The bare `.replace()` this walk
    // replaced passed `undefined` for group 1 too — the `string` typing on
    // `replaceTag` was already a lie, just one TS could not see.
    r += replaceTag(
      collected[0],
      collected[1] ?? '',
      collected[2] ?? '',
      collected[3] ?? '',
      collected[4] ?? '',
    );
    // Taken from the match itself, not from `tagRe.lastIndex`, which is no
    // longer positioned on this match now that the walk has finished.
    cursor = collected.index + collected[0].length;
  }
  r += neutralise(source.slice(cursor));

  for (let i = stack.length - 1; i > -1; i -= 1) {
    r += `</${stack[i]}>`;
  }

  return {
    errors,
    value: r,
  };
};

export const sanitizeHtmlWithErrors = (
  str: string,
  argOptions?: Options,
): {
  errors: ReadonlyArray<string>;
  value: string;
} => sanitizeHtmlWithErrorsUsing(tagRe, str, argOptions);

export const sanitizeHtml = (str: string, argOptions?: Options): string => {
  const { value } = sanitizeHtmlWithErrors(str, argOptions);
  return value;
};

// Test-only escape hatch, not part of the package's contract. The suite uses it
// to pin the safety properties on BOTH tokenizer branches: without it, the
// fallback branch (the one Safari <= 16.3 actually executes) would ship with
// zero coverage, since CI can only ever build the lookbehind variant.
export const __forTests = {
  buildTagRe,
  separators: TAG_RE_SEPARATORS,
  sanitizeHtmlWithErrorsUsing,
};
